import React from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import Link from "next/link";
import FreeBadgeIcon from "@/assets/icons/FreeBadgeIcon";
import MoneyBackIcon from "@/assets/icons/MoneyBackIcon";
import WarrantyIcon from "@/assets/icons/WarrantyIcon";
import NativeHapticsIcon from "@/assets/icons/NativeHapticsIcon";
import MeshLiningIcon from "@/assets/icons/MeshLiningIcon";
import { useUniformWrap } from "@/components/tactsuit/components/buy-tactsuit/use-uniform-wrap";
import { useGeRouted } from "@/hooks/useGeRouted";

// 아이콘 크기: **20 고정**(#5290, 시안 29187:45962 등). 종전엔 모바일 16 / 데스크톱 24 였다.
//
// 시안의 박스는 20 이고 그 안의 글리프가 16.667 이다 = 24 짜리 아이콘 컴포넌트를 20 박스로
// 축소한 것(16.667 = 20 × 20/24). 우리 아이콘도 24 viewBox 라 `size-5` 로 렌더하면 같은
// 비율이 나온다 — 별도 래퍼로 글리프를 다시 줄이면 안 된다.
//
// 색은 **텍스트와 한 단계 다르다** — Figma 아이콘 #4D5358(coolgray-70),
// 텍스트 #21272A(coolgray-90). 아이콘 컴포넌트는 `currentColor` 를 쓰므로 여기서 지정한다.
const ICON = "size-5 shrink-0 text-coolgray-70";

/**
 * 결제 버튼 아래 작은 신뢰 배지 (무료 반품 / 보증 / 무료 배지)
 *
 * ⚠️ `showFreeBadge` 는 **필수 prop 이다.** 원래는 기본값 `false`(fail-closed)였는데,
 * 그 "안전한" 기본값이 정확히 사고를 냈다 — `/cart` 가 `<TrustBadges />` 로 불러서
 * 조끼를 담아도 항목이 통째로 사라졌고, `tsc` 도 테스트도 아무 말이 없었다. PDP 에서
 * "무료 원본 배지 포함"을 보고 담은 사용자가 카트에서 그 약속을 못 보는 상태였다.
 * 필수로 두면 새 호출처가 생길 때마다 컴파일러가 판정을 강제한다.
 *
 * 무료 원본 배지는 조끼(TactSuit) 계열에만 들어간다. 없는 혜택을 약속하면 안 되므로
 * 호출처는 **반드시 데이터로 판정**할 것 — `libs/cart/has-free-badge-device`.
 *
 * 판정 기준은 `badgeAccessories.length > 0` — ProductFeatureSections 의
 * "Free Original Badge Inside" 혜택 카드와 **같은 기준**을 써서 한쪽만 고쳐지는 걸 막는다.
 * (develop 은 하드코딩 slug 목록 `hasProductType(VEST_SLUGS)` 로 게이트했다. 실측상 두
 * 기준의 결과가 일치했고 — Pro·Air·Air Camo + 조끼 포함 번들만 true, 슬리브·바이저·
 * Tactosy·DK3 는 false — 데이터 파생이 신규 조끼에 자동 대응하므로 이쪽을 택했다.
 * 그 slug 목록과 ProductBenefits 는 렌더 경로를 잃어 삭제됐다.)
 */
// 텍스트: Figma `Body/Small - Noto Sans` = 12px / lh 16px / ls 0.4px / w400, coolgray-90.
// 데스크톱·모바일 동일. `text-sm` 이 이 프로젝트에선 ["12px","16px"] 이라 폰트+lh 를 한 번에
// 만족한다(기본 tailwind 의 14px 아님 — 커스텀 스케일). Noto Sans·400 은 전역 기본이라 생략.
// ⚠️ `text-[12px]` 로 쓰면 line-height 가 안 따라온다.
export function TrustBadges({
  showFreeBadge,
  items,
}: {
  showFreeBadge: boolean;
  /**
   * 이 제품 전용 배지(번들 전용 PDP — `BUNDLE_DETAIL_RAIL[].trustBadges`). 주면 기본 세 칸 대신 이것만 그린다
   * (링크 없음 · GE 숨김 없음 — 혜택 약속이 아니라 구성 소개다).
   */
  items?: {
    icon: "nativeHaptics" | "freeBadge" | "meshLining" | "returns" | "warranty";
    label: string;
  }[];
}) {
  // "Warranty" 는 support.json, "Free Badge" 는 tactsuit.json 에 7개 locale 완역이
  // 이미 있다(실측 확인). PDP 는 네 namespace 를 모두 SSR 로드한다
  // (`getServerSideProductProps` — tactsuit·gnb·shop·support).
  //
  // ⚠️ **namespace 배열만 넓히는 걸로는 안 된다.** react-i18next 는 배열의 첫
  // namespace 를 기본으로 쓸 뿐이고, 이 프로젝트엔 `fallbackNS` 설정이 없다
  // (next-i18next.config.js). 그래서 `t("Warranty")` 는 shop.json 만 보고,
  // 없으면 defaultValue(영어)로 떨어진다 — 실측: ko/ja/de 전부 "Warranty" 영어.
  // → 키가 실제로 있는 namespace 를 **명시**해야 한다.
  const { t } = useTrans(["shop", "tactsuit", "support"]);

  const freeBadge = t("Free Badge", {
    ns: "tactsuit",
    defaultValue: "Free Badge",
  });
  const freeReturns = t("Free returns", { defaultValue: "Free returns" });
  const warranty = t("Warranty", { ns: "support", defaultValue: "Warranty" });

  // 하나라도 랩되면 세 항목 전부 세로 배치 — 시안 케이스 3 은 짧은 라벨까지 세로다.
  // 라벨 문자열을 deps 로 넘겨 locale 전환 시 재측정한다.
  const { ref, stacked } = useUniformWrap(
    items ? items.map((i) => i.label) : [freeBadge, freeReturns, warranty],
  );

  /**
   * ★GE 국제 체크아웃 대상이면 Free Returns 를 **그리지 않는다** (#5829 · GE 요청).
   * 판정은 카트 라우팅·배송비 숨김과 같은 단일 출처 `useGeRouted()`(플래그 ON + 비-KR).
   *
   * ⚠️ **GE 의 숨김 CSS 를 쓰지 않는다.** GE 가 안내한 `ge-hide` 클래스는 Shopify 전용이고,
   *    Bespoke 문서의 `data-ge-hide` 는 우리 머천트 CSS 가 `visibility:hidden` 이라 **자리가
   *    남았다**(3등분 가운데 155px 빈칸 · dev 실측 2026-09-28). 우리가 그리지 않으면 빈칸도
   *    GE CSS 로드 실패도 없다(#5833 리뷰 R1 Minor). 자세히는 `docs/global-e-storefront.md`
   *    Element Visibility.
   *
   * 플래그 로딩 중엔 `false` 라 **먼저 보였다가** GE 국가면 사라진다 — 국내(KR) 고객에게
   * 깜빡이며 사라지는 쪽보다 낫다. GE CSS 방식도 스크립트 로드 뒤라 같은 깜빡임이 있었다.
   */
  const geRouted = useGeRouted();

  if (items) {
    const icons = {
      nativeHaptics: <NativeHapticsIcon className={ICON} />,
      freeBadge: <FreeBadgeIcon className={ICON} />,
      meshLining: <MeshLiningIcon className={ICON} />,
      returns: <MoneyBackIcon className={ICON} />,
      warranty: <WarrantyIcon className={ICON} />,
    };
    return (
      <div ref={ref} className="flex rounded-lg bg-coolgray-10 py-2">
        {items.map((item) => (
          <Item key={item.label} icon={icons[item.icon]} stacked={stacked}>
            {item.label}
          </Item>
        ))}
      </div>
    );
  }

  return (
    // 시안 `Benefits`(29187:45960) — 회색 박스 bg coolgray-10 / radius 8 / **py 8 만**.
    // 좌우 패딩은 컨테이너가 아니라 각 셀의 px-1(4)이 갖는다.
    //
    // ## `justify-between` → 3등분 (#5290)
    //
    // 종전엔 `justify-between` 이었고, 항목이 2개로 줄면 갭이 220px 넘게 벌어져서 "갭 상한
    // 64px" 규칙(2개면 `justify-center gap-16`)을 따로 뒀다. 디자이너가 그 방식을 바꾼 이유가
    // 이슈에 그대로 적혀 있다 — **한국어처럼 단어가 짧으면 갭이 멀어 보인다**("무료 배지"
    // 5자 vs "Free Badge" 10자).
    //
    // 3등분은 그 두 문제를 함께 없앤다. 항목이 2개면 각 셀이 1/2 을 먹어 자연히 중앙에
    // 모이므로 **개수별 분기가 필요 없다** — 그래서 종전 `showFreeBadge` 기반 레이아웃
    // 분기를 지웠다(prop 자체는 항목 표시 여부로 계속 필요하다).
    // ⚠️ `items-center` 가 아니다. 시안은 셀이 `self-stretch` + 내부 Item 이 `h-full` 이라
    // **셀이 행 높이를 채운다.** `items-center` 로 두면 셀이 내용 높이가 되고, 세로 배치에서
    // 라벨 줄 수가 다른 셀들이 각자 수직 중앙에 놓여 **아이콘 높이가 어긋난다**
    // (실측 de/1440: 아이콘 top 8 / 16 / 16). 기본값 stretch 를 그대로 쓴다.
    <div ref={ref} className="flex rounded-lg bg-coolgray-10 py-2">
      {showFreeBadge && (
        <Item icon={<FreeBadgeIcon className={ICON} />} stacked={stacked}>
          {freeBadge}
        </Item>
      )}
      {/* ★GE 운영 국가 고객에게는 숨긴다 (#5829).
          근거는 **GE 의 요청**이다 — GE 가 이 배지를 「국내 전용 혜택」으로 분류해 해외
          고객에게 숨겨 달라고 했다(2026-09-28). 반품 쪽 요청은 이 배지뿐이다.
          ⚠️ 「GE 가 판매자(MoR)라 반품도 GE 조건」은 **추론**이다(판매자 사실은
          `docs/global-e-email-templates.md`, 그 결과 반품 조건이 달라지는지는 미확인).
          같은 약관으로 링크하는 형제 표면 셋 — 45일 보장 카드(`ProductFeatureSections` ·
          `StartWithConfidence` · `PurchaseBenefitsFull`) — 은 **그대로 둔다**(작성자 결정
          2026-09-29). 이 배지와 달리 **뜻이 명확하다** — 「45일 안 환불 보장」이라 비용을
          약속하지 않고, PDP 카드는 「Return shipping costs are the customer’s
          responsibility.」 경고까지 붙어 있다. 문제는 「Free」가 반품 **배송비 무료**로
          읽히는 이 배지 쪽이었다. GE 도 이 카드들은 요청하지 않았다.
          우리가 판매자인 주문(KR · 비-GE)에서는 그대로 보인다 — 기획 의도(반품이 쉽다는
          강조)는 거기서 유효하다. 판정은 위 `geRouted` 주석. */}
      {!geRouted && (
        <Item
          icon={<MoneyBackIcon className={ICON} />}
          href="/legals/cancellation-return-refund/"
          stacked={stacked}
        >
          {freeReturns}
        </Item>
      )}
      <Item icon={<WarrantyIcon className={ICON} />} stacked={stacked}>
        {warranty}
      </Item>
    </div>
  );
}

/**
 * 셀 하나. **셀이 링크를 감싼다** — 반대로 하면(종전 구조) 링크가 flex 자식이 되어
 * 3등분 대상이 링크가 되고, 링크 없는 두 항목과 폭이 어긋난다.
 */
const Item = ({
  icon,
  href,
  stacked,
  children,
}: React.PropsWithChildren<{
  icon: React.ReactNode;
  href?: string;
  /**
   * 행 전체를 세로 배치할지. **셀이 스스로 판정하지 않는다** — 시안은 "이 셀이 랩됐나" 가
   * 아니라 "행에 랩된 셀이 있나" 를 기준으로 하므로 부모가 재서 내려준다
   * (`use-uniform-wrap.ts`).
   */
  stacked: boolean;
}>) => {
  // 시안 `Item`(29187:45961 이하) — 셀은 `flex-[1_0_0] min-w-px px-[4px]`, 높이 FILL.
  // `min-w-0` 이 없으면 긴 라벨(de)이 셀을 밀어내 3등분이 깨진다.
  //
  // ## 줄바꿈: 아이콘 위 / 텍스트 아래
  //
  // 두 겹으로 동작한다.
  //  1. `flex-wrap` — 라벨이 아이콘 옆에 못 들어가면 flex 가 다음 줄로 내린다. 이게
  //     **판정의 근거**이기도 하다(부모가 이 상태를 읽어 `stacked` 를 정한다).
  //  2. `stacked` — 하나라도 내려갔으면 부모가 세 셀 **전부** `flex-col` 로 바꾼다.
  //     시안 케이스 3(29187:45988)이 "Garantie"(8자)까지 세로로 그려서 그렇게 맞춘 것.
  //
  // 근거(1): flex 는 줄 나눔을 **축소 전 크기(hypothetical main size = max-content)** 로
  // 판정하고, 축소는 줄이 확정된 뒤에 한다. 그래서 긴 라벨은 먼저 다음 줄로 내려가고,
  // 그 줄에서 셀 폭까지 좁아지며 내부적으로 2줄이 된다.
  //
  // ⚠️ 텍스트에 `shrink-0` 을 주지 말 것. 줄바꿈 자체는 되지만 **좁아지지 않아 셀을
  // 넘친다** — 실측(390, de): 텍스트 150px vs 셀 119px, overflow. 1440 에서는 셀이 155 라
  // 우연히 안 넘쳐서 데스크톱만 보면 놓친다.
  const body = (
    <>
      {/* 아이콘 크기·색은 `ICON` 상수가 갖는다(20 고정, coolgray-70).
          따로 래퍼를 씌워 글리프를 다시 줄이지 말 것 — 시안 비율이 깨진다. */}
      {icon}
      {/* 라벨 12px (`text-sm` = 12/16, 커스텀 스케일 — `text-[12px]` 는 lh 가 안 따라온다).
          자간 0.4, 색 coolgray-90. 줄바꿈된 줄에서 가운데 정렬돼야 하므로 `text-center`. */}
      <span className="text-center text-sm tracking-[0.4px]">{children}</span>
    </>
  );

  // 세로 갭 4(시안 `gap-[4px]`) / 가로 갭 8(`gap-[8px]`) — `gap-x-2 gap-y-1` 한 벌로 덮는다.
  //
  // ⚠️ `stacked` 여도 `flex-wrap` 은 남긴다. 부모가 매 측정마다 가로 배치로 되돌린 뒤
  // 읽으므로(`use-uniform-wrap.ts`), 그 순간 줄바꿈이 실제로 일어나야 판정이 된다.
  const cell = cn(
    "flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-2 gap-y-1 px-1 text-coolgray-90",
    // 세로 배치에서는 `justify-*` 가 **수직** 축이 된다. `justify-center` 를 그대로 두면
    // 라벨이 1줄인 셀의 내용이 수직 중앙으로 내려가 아이콘 줄이 어긋난다 → 위 정렬.
    // 시안 랩 케이스 Item(29187:45990 등)도 `justify` 미지정 = flex-start 다.
    stacked && "flex-col justify-start",
  );

  if (href) {
    // ⚠️ 종전엔 `hover:text-coolgray-90` 이 붙어 있었는데 기본 색이 이미 coolgray-90 이라
    // **무동작**이었다(같은 색으로 바뀜). 있는 척하는 코드라 지운다.
    //
    // 시안에는 이 항목의 링크 스타일이 없다 — 밑줄도, hover 색도 지정돼 있지 않다. 그래서
    // 지금은 커서 말고 링크라는 신호가 없다. 어떤 어포던스를 줄지는 디자인 결정이라
    // #5290 에 물어 둔 상태다. 임의로 밑줄을 넣지 않는다.
    return (
      <Link href={href} className={cell}>
        {body}
      </Link>
    );
  }

  return <span className={cell}>{body}</span>;
};
